'use client';

import { useEffect } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { LocationPin } from './location-pin.component';

import { SearchEntityInput } from '@/app/(main)/(pages)/busca/[slug]/_components/search-entity-input/search-entity-input.component';
import { SearchSort } from '@/app/(main)/(pages)/busca/[slug]/_components/search-sort/search-sort.component';
import { SearchFilters } from '@/app/(main)/(pages)/busca/[slug]/_components/search-filters/search-filters.component';
import { ClearAllFilters } from '@/app/(main)/(pages)/busca/[slug]/_components/search-filters/clear-all-filters.component';
import { SearchSortType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-sort.type';
import { useSearchFilter } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-search-filters.hook';
import { ContentContainer } from '@/components/ui/content-container.component';
import { CodesOrSlugsResponse } from '@/app/(main)/(pages)/busca/[slug]/_types/codes-or-slugs-response.type';
import { SearchType } from '@/types/most-search-type';
import { getSelectionText } from '@/app/(main)/(pages)/busca/[slug]/_consts/get-selection-text.consts';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useSearchHeaderScroll } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-search-header-scroll.hook';
import { getSearchModality } from '@/app/(main)/(pages)/busca/[slug]/_utils/search-modality.util';
import { handleOpenSearchModal } from '@/store/search/handle-open-search-modal';
import { openSearchModalAtStep } from '@/store/search/search.store';
import { ProgressiveSearchModalKeys } from '@/app/(main)/_components/search-modal/search-modal.types';
import { headerStore } from '@/store/header.store';
import { cn } from '@/utils/cn-tw-merge';

interface HeaderProps {
  procedures: CodesOrSlugsResponse;
  isLaboratorialSearch?: boolean;
  isVaccineSearch?: boolean;
}

export default function SearchHeader({ procedures, isLaboratorialSearch, isVaccineSearch }: HeaderProps) {
  useSearchHeaderScroll();

  const isHeaderHidden = useStore(headerStore, (state) => state.isScrolled);
  const { filters, setFilters } = useSearchFilter();

  const isExamOrVaccineSearch = Boolean(isLaboratorialSearch || isVaccineSearch);

  const handleOpen = () => {
    if (!isExamOrVaccineSearch) {
      handleOpenSearchModal();
      return;
    }

    openSearchModalAtStep(
      ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS,
      procedures.map((procedure) => ({
        id: procedure.id,
        code: procedure.code,
        displayName: procedure.name,
      })),
    );
  };

  const procedure = procedures[0];
  const isTelehealth = procedure.searchType === SearchType.ONLINE_CONSULTATION;
  const hasLocation = isLaboratorialSearch ? true : !isTelehealth;
  const hiddenSortOptions = isTelehealth ? [SearchSortType.DISTANCE] : [];
  const orderBy = hiddenSortOptions.includes(filters.orderBy) ? SearchSortType.RELEVANCE : filters.orderBy;
  const modality = getSearchModality({
    searchType: procedure.searchType,
    isLaboratorialSearch,
    isVaccineSearch,
  });
  const titleInput = isVaccineSearch
    ? getSelectionText(procedures.length, 'vacina', 'vacinas', 'selecionada')
    : isLaboratorialSearch
      ? getSelectionText(procedures.length, 'exame', 'exames', 'selecionado')
      : procedure.name;
  const isSingleProcedure = procedures.length === 1;
  const title = isVaccineSearch
    ? isSingleProcedure
      ? 'Vacina'
      : 'Vacinas'
    : isLaboratorialSearch
      ? isSingleProcedure
        ? 'Exame laboratorial'
        : 'Exames laboratoriais'
      : procedure.name;

  useEffect(() => {
    if (orderBy !== filters.orderBy) {
      setFilters({ orderBy });
    }
  }, [orderBy, filters.orderBy, setFilters]);

  return (
    <>
      <div
        className={cn(
          'w-full flex justify-center bg-white py-3 border-b border-gray-300 sticky z-10 transition-[top] duration-300 ease-in-out',
          isHeaderHidden ? 'top-0' : 'top-16 md:top-14',
        )}
      >
        <ContentContainer className="md:px-0 md:mx-auto md:max-w-[576px] lg:max-w-3xl">
          <SearchEntityInput
            value={titleInput}
            onOpen={handleOpen}
          />
        </ContentContainer>
      </div>

      <div className="w-full flex justify-center bg-gray-50 py-6">
        <ContentContainer className="md:px-0 md:mx-auto md:max-w-[576px] lg:max-w-3xl flex flex-col gap-2">
          {hasLocation && <LocationPin />}
          <Typography
            variant={TypographyVariant.MODAL_TITLE}
            className="break-words"
            data-testid={DataTestIdEnum.SEARCH_PAGE_TITLE}
          >
            {title}
          </Typography>
          <div
            className="no-scrollbar -mx-6 flex gap-2 overflow-x-auto px-6 sm:-mx-16 sm:px-16 md:mx-0 md:px-0"
            data-testid={DataTestIdEnum.SEARCH_HEADER_PILL_ROW}
          >
            <SearchSort
              value={orderBy}
              onChange={(next) => setFilters({ orderBy: next })}
              modality={modality}
              hiddenOptions={hiddenSortOptions}
              className="shrink-0"
            />
            <SearchFilters
              slugs={procedures.map((procedure) => procedure.slug)}
              modality={modality}
              className="shrink-0"
            />
            <ClearAllFilters modality={modality} className="shrink-0" />
          </div>
        </ContentContainer>
      </div>
    </>
  );
}
